/**
 * ProgrammeReportPage.tsx — automated per-programme outcome report (VER-109).
 *
 * Route: /assess/report/:programmeId
 *
 * Consumes the group-level reporting endpoint (GET /survey-report/programme/:id) and
 * renders one OutcomeScaleCard per outcome scale, plus an authenticated raw-CSV export.
 *
 * ANONYMITY (VER-109): everything on screen is GROUP-LEVEL. Individual participant
 * scores are exposed only through the raw CSV download, which is what the caption and
 * the export button make explicit.
 *
 * PR #5 review (Sean):
 *  * The CSV button is shown only to holders of `SurveyReportExport` (Manager / Admin). The server
 *    enforces it; hiding the button stops offering a practitioner a download that will 403.
 *  * Group proxy reads get their own section below the paired scales — see GroupObservationsCard.
 *
 * TODO [RBAC]: the route is currently guarded with permission="DashboardView" (see
 * Routes.tsx) — swap to the SurveyReportView permit (already enforced server-side)
 * once it is added to the frontend permission set. Run verbal-rbac-review before merge.
 */
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Divider, Stack, Typography } from "@mui/material";
import { ArrowBackOutlined, DownloadOutlined } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "../../layout/Spinner";
import { SimpleBlankSlate } from "../../../core/components/SimpleBlankSlate";
import {
    downloadProgrammeSurveyCsv,
    getProgrammeOutcomeReports,
    GroupObservationScale,
    ProgrammeOutcomeReport,
} from "../../../client/api/survey";
import { get as getProgramme } from "../../../client/api/programme";
import { useAuth } from "../../../contexts/auth.context";
import { OutcomeScaleCard } from "./OutcomeScaleCard";
import { GroupObservationsCard } from "./GroupObservationsCard";

export const ProgrammeReportPage = (): JSX.Element => {
    const { programmeId } = useParams<{ programmeId: string }>();
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const id = Number(programmeId);
    // The raw export carries every participant's own answers — the individual data this screen
    // suppresses below the small-group floor. Manager / Admin only; the server enforces the same.
    const canExportCsv = hasPermission("SurveyReportExport");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [reports, setReports] = useState<ProgrammeOutcomeReport[]>([]);
    const [groupObservations, setGroupObservations] = useState<GroupObservationScale[]>([]);
    const [programmeName, setProgrammeName] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;

        if (!Number.isInteger(id) || id <= 0) {
            setError("Invalid programme reference.");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        // Report is the primary payload; the programme name is best-effort for the header.
        Promise.all([getProgrammeOutcomeReports(id), getProgramme(id, {}).catch(() => null)])
            .then(([reportsResponse, programme]) => {
                if (!active) return;
                setReports(reportsResponse.reports);
                // Defaulted rather than assumed: an older API build has no such field, and reading
                // `undefined.map` would take the whole report down over an optional section.
                setGroupObservations(reportsResponse.groupObservations ?? []);
                setProgrammeName(programme?.name ?? null);
            })
            .catch(() => {
                if (!active) return;
                setError(
                    "We couldn't load this programme's outcome report. It may not exist or you may not have access.",
                );
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [id]);

    const handleDownloadCsv = async (): Promise<void> => {
        setDownloadError(null);
        setDownloading(true);
        try {
            await downloadProgrammeSurveyCsv(id);
        } catch {
            setDownloadError("The raw CSV export could not be downloaded. Please try again.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box p={4} width={1}>
            {/* Header + back navigation */}
            <Button
                variant="text"
                color="primary"
                startIcon={<ArrowBackOutlined />}
                onClick={() => navigate("/assess/reports")}
                sx={{ mb: 2 }}
            >
                All programmes
            </Button>

            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={2}
                mb={1}
            >
                <Box>
                    <Typography variant="overline" color="textSecondary">
                        Programme outcome report
                    </Typography>
                    <Typography variant="h3" component="h1">
                        {programmeName ?? "Programme"}
                    </Typography>
                </Box>
                {canExportCsv ? (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadOutlined />}
                        onClick={handleDownloadCsv}
                        disabled={loading || downloading || !!error}
                    >
                        {downloading ? "Preparing CSV…" : "Download raw data (CSV)"}
                    </Button>
                ) : null}
            </Stack>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {canExportCsv
                    ? "Group-level results only. Individual participant scores are available in the raw CSV export for analysis in SPSS or Jamovi."
                    : "Group-level results only. Individual participant scores are held in the raw data export, which a manager can provide."}
            </Typography>

            {downloadError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
                    {downloadError}
                </Alert>
            )}

            <Divider sx={{ mb: 3 }} />

            {loading ? (
                <Spinner />
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : reports.length === 0 && groupObservations.length === 0 ? (
                <SimpleBlankSlate
                    header="No outcome data yet"
                    content="This programme has no paired outcome-scale results yet. A report appears once participants have completed an outcome measure at least twice (a baseline and a later administration)."
                />
            ) : (
                <>
                    {reports.map((report) => (
                        <OutcomeScaleCard key={report.scaleCode} report={report} />
                    ))}

                    {groupObservations.length > 0 ? (
                        <Box mt={reports.length > 0 ? 4 : 0}>
                            <Typography variant="h4" component="h2" mb={1}>
                                Group observations
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={2}>
                                Readings a facilitator recorded for the whole group, where participants had no devices
                                of their own. They are shown separately because they are not individual self-reports and
                                cannot be paired into the results above.
                            </Typography>
                            {groupObservations.map((scale) => (
                                <GroupObservationsCard key={scale.scaleCode} scale={scale} />
                            ))}
                        </Box>
                    ) : null}
                </>
            )}
        </Box>
    );
};
